import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  ServerStackIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import adminService from '../../services/admin.service';
import {
  AdminUserDetail as AdminUserDetailType,
  AdminUserUpdate,
  AdminSFTPInfo,
  AdminUsageBreakdown,
  AdminIngestionSourceItem,
  AdminTimeline,
  AdminUserPricingResponse,
  AdminCustomPricingCreate,
} from '../../types/admin.types';
import ConfirmationDialog from '../../components/shared/ConfirmationDialog';
import { PricingFieldChange } from '../../components/admin/PricingChangeConfirmation';
import CostToServePanel from '../../components/admin/CostToServePanel';
import EvaluationCriteriaEditor from '../../components/admin/EvaluationCriteriaEditor';

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
};

const SFTP_STATUS_BADGE: Record<string, { className: string; label: string }> = {
  provisioned: { className: 'bg-green-100 text-green-800', label: 'Provisioned' },
  pending: { className: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  failed: { className: 'bg-red-100 text-red-800', label: 'Failed' },
};

const SOURCE_TYPE_BADGE: Record<string, { className: string; label: string }> = {
  sftp: { className: 'bg-blue-100 text-blue-800', label: 'SFTP' },
  api_batch: { className: 'bg-indigo-100 text-indigo-800', label: 'API Batch' },
  web_upload: { className: 'bg-gray-100 text-gray-800', label: 'Web Upload' },
};

const SOURCE_STATUS_BADGE: Record<string, { className: string; label: string }> = {
  active: { className: 'bg-green-100 text-green-800', label: 'Active' },
  paused: { className: 'bg-yellow-100 text-yellow-800', label: 'Paused' },
  disabled: { className: 'bg-gray-100 text-gray-800', label: 'Disabled' },
  error: { className: 'bg-red-100 text-red-800', label: 'Error' },
};

// Tier defaults matching TIER_PRICING in app/schemas/billing.py
const TIER_DEFAULTS: Record<string, { price: number; minutes: number; overageRate: number; overageCap: number }> = {
  free:    { price: 0,  minutes: 500,  overageRate: 0,     overageCap: 2500 },
  starter: { price: 29, minutes: 1000, overageRate: 0.018, overageCap: 2500 },
  growth:  { price: 99, minutes: 5000, overageRate: 0.018, overageCap: 2500 },
};

// Validation constraints matching backend Pydantic ge/le bounds
const PRICING_LIMITS = {
  custom_base_price:       { min: 0, max: 1000000,  label: 'Base Price' },
  custom_minutes_included: { min: 0, max: 50000000, label: 'Included Minutes' },
  custom_overage_rate:     { min: 0, max: 1,         label: 'Overage Rate' },
  custom_overage_cap:      { min: 0, max: 50000000, label: 'Overage Cap' },
} as const;

type PricingField = keyof typeof PRICING_LIMITS;

function validatePricingField(field: PricingField, value: number | undefined | null): string | null {
  if (value === undefined || value === null) return null;
  const limits = PRICING_LIMITS[field];
  if (value < limits.min) return `${limits.label} cannot be negative`;
  if (value > limits.max) return `${limits.label} cannot exceed ${limits.max.toLocaleString()}`;
  return null;
}

function calculateBreakdown(
  usage: number,
  basePrice: number,
  included: number,
  overageRate: number,
  overageCap: number,
): { total: number; overageMinutes: number; overageRevenue: number } {
  const overageMinutes = Math.min(Math.max(0, usage - included), overageCap);
  const overageRevenue = overageMinutes * overageRate;
  return { total: basePrice + overageRevenue, overageMinutes, overageRevenue };
}

function buildPricingChanges(
  form: AdminCustomPricingCreate,
  currentPricing: { custom_base_price: string | null; custom_minutes_included: string | null; custom_overage_rate: string | null; custom_overage_cap: number | null } | null,
  tierDefaults: { price: number; minutes: number; overageRate: number; overageCap: number },
): PricingFieldChange[] {
  const before = {
    basePrice: currentPricing?.custom_base_price ? parseFloat(currentPricing.custom_base_price) : tierDefaults.price,
    minutes: currentPricing?.custom_minutes_included ? parseFloat(currentPricing.custom_minutes_included) : tierDefaults.minutes,
    overageRate: currentPricing?.custom_overage_rate ? parseFloat(currentPricing.custom_overage_rate) : tierDefaults.overageRate,
    overageCap: currentPricing?.custom_overage_cap ?? tierDefaults.overageCap,
  };

  const after = {
    basePrice: form.custom_base_price ?? tierDefaults.price,
    minutes: form.custom_minutes_included ?? tierDefaults.minutes,
    overageRate: form.custom_overage_rate ?? tierDefaults.overageRate,
    overageCap: form.custom_overage_cap ?? tierDefaults.overageCap,
  };

  return [
    { label: 'Base Price', beforeValue: before.basePrice, afterValue: after.basePrice, format: 'currency', unit: '/mo', favorableDirection: 'down' },
    { label: 'Included Minutes', beforeValue: before.minutes, afterValue: after.minutes, format: 'integer', unit: 'min', favorableDirection: 'up' },
    { label: 'Overage Rate', beforeValue: before.overageRate, afterValue: after.overageRate, format: 'currency_rate', unit: '/min', favorableDirection: 'down' },
    { label: 'Overage Cap', beforeValue: before.overageCap, afterValue: after.overageCap, format: 'integer', unit: 'min', favorableDirection: 'up' },
  ];
}

const TIMELINE_ICONS: Record<string, { color: string }> = {
  registration: { color: 'bg-indigo-500' },
  verified: { color: 'bg-green-500' },
  sftp_provisioned: { color: 'bg-blue-500' },
  subscription_created: { color: 'bg-purple-500' },
  subscription_canceled: { color: 'bg-red-500' },
  api_key_created: { color: 'bg-orange-500' },
  first_analysis: { color: 'bg-green-500' },
  milestone_10: { color: 'bg-amber-500' },
  milestone_100: { color: 'bg-amber-600' },
};

type PendingAction =
  | { type: 'toggle_admin' }
  | { type: 'toggle_active' }
  | { type: 'toggle_verified' }
  | { type: 'send_password_reset' }
  | { type: 'sftp_retry' }
  | { type: 'sftp_reset_password' }
  | { type: 'toggle_source'; sourceId: string; currentStatus: string }
  | { type: 'change_tier'; newTier: string };

const CONFIRMATION_CONFIG: Record<string, { variant: 'danger' | 'warning' | 'default'; confirmLabel: string }> = {
  toggle_admin: { variant: 'danger', confirmLabel: 'Confirm' },
  toggle_active: { variant: 'danger', confirmLabel: 'Confirm' },
  toggle_verified: { variant: 'warning', confirmLabel: 'Confirm' },
  send_password_reset: { variant: 'warning', confirmLabel: 'Send Reset Email' },
  sftp_retry: { variant: 'default', confirmLabel: 'Retry' },
  sftp_reset_password: { variant: 'warning', confirmLabel: 'Reset Password' },
  toggle_source: { variant: 'warning', confirmLabel: 'Confirm' },
  change_tier: { variant: 'default', confirmLabel: 'Change Tier' },
};

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // SFTP state
  const [sftp, setSftp] = useState<AdminSFTPInfo | null>(null);
  const [sftpLoading, setSftpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Usage breakdown state
  const [usage, setUsage] = useState<AdminUsageBreakdown | null>(null);

  // Ingestion sources state
  const [sources, setSources] = useState<AdminIngestionSourceItem[] | null>(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);

  // Timeline state
  const [timeline, setTimeline] = useState<AdminTimeline | null>(null);

  // Custom pricing state
  const [pricing, setPricing] = useState<AdminUserPricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [pricingForm, setPricingForm] = useState<AdminCustomPricingCreate>({});
  const [confirmRemovePricing, setConfirmRemovePricing] = useState(false);
  const [pricingErrors, setPricingErrors] = useState<Record<string, string>>({});
  // Pricing confirmation modal state
  const [showPricingConfirmation, setShowPricingConfirmation] = useState(false);

  // Confirmation dialog state
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchAll = async () => {
      try {
        const [detail, sftpInfo, usageData, sourcesData, timelineData, pricingData] = await Promise.allSettled([
          adminService.getUserDetail(id),
          adminService.getSFTPInfo(id),
          adminService.getUsageBreakdown(id),
          adminService.getUserIngestionSources(id),
          adminService.getUserTimeline(id),
          adminService.getUserPricing(id),
        ]);

        if (detail.status === 'fulfilled') setUser(detail.value);
        if (sftpInfo.status === 'fulfilled') setSftp(sftpInfo.value);
        if (usageData.status === 'fulfilled') setUsage(usageData.value);
        if (sourcesData.status === 'fulfilled') setSources(sourcesData.value);
        if (timelineData.status === 'fulfilled') setTimeline(timelineData.value);
        if (pricingData.status === 'fulfilled') setPricing(pricingData.value);
      } catch {
        toast.error('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id]);

  const handleUpdate = async (updates: AdminUserUpdate) => {
    if (!id) return;
    setUpdating(true);
    try {
      const updated = await adminService.updateUser(id, updates);
      setUser(updated);
      toast.success('User updated');
    } catch {
      toast.error('Failed to update user');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingAction || !id) return;
    setActionLoading(true);
    try {
      switch (pendingAction.type) {
        case 'toggle_admin':
          await handleUpdate({ is_admin: !user?.is_admin });
          break;
        case 'toggle_active':
          await handleUpdate({ is_active: !user?.is_active });
          break;
        case 'toggle_verified':
          await handleUpdate({ is_verified: !user?.is_verified });
          break;
        case 'send_password_reset':
          await adminService.sendPasswordReset(id);
          toast.success('Password reset email sent');
          break;
        case 'sftp_retry':
          setSftpLoading(true);
          await adminService.retrySFTPProvisioning(id);
          toast.success('SFTP provisioning retry queued');
          const retryInfo = await adminService.getSFTPInfo(id);
          setSftp(retryInfo);
          setSftpLoading(false);
          break;
        case 'sftp_reset_password':
          setSftpLoading(true);
          await adminService.resetSFTPPassword(id);
          toast.success('SFTP password reset queued');
          const resetInfo = await adminService.getSFTPInfo(id);
          setSftp(resetInfo);
          setSftpLoading(false);
          break;
        case 'toggle_source': {
          const newStatus = pendingAction.currentStatus === 'active' ? 'paused' : 'active';
          setSourcesLoading(true);
          await adminService.updateIngestionSource(id, pendingAction.sourceId, newStatus);
          toast.success(`Source ${newStatus === 'active' ? 'resumed' : 'paused'}`);
          const updated = await adminService.getUserIngestionSources(id);
          setSources(updated);
          setSourcesLoading(false);
          break;
        }
        case 'change_tier': {
          await adminService.changeSubscriptionTier(id, { new_tier: pendingAction.newTier });
          toast.success(`Tier changed to ${TIER_LABELS[pendingAction.newTier] || pendingAction.newTier}`);
          const updatedUser = await adminService.getUserDetail(id);
          setUser(updatedUser);
          break;
        }
      }
    } catch {
      const errorMessages: Record<string, string> = {
        toggle_admin: 'Failed to update user',
        toggle_active: 'Failed to update user',
        toggle_verified: 'Failed to update user',
        send_password_reset: 'Failed to send password reset',
        sftp_retry: 'Failed to retry SFTP provisioning',
        sftp_reset_password: 'Failed to reset SFTP password',
        toggle_source: 'Failed to update ingestion source',
        change_tier: 'Failed to change tier',
      };
      toast.error(errorMessages[pendingAction.type]);
      setSftpLoading(false);
      setSourcesLoading(false);
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  };

  const getConfirmationTitle = (): string => {
    if (!pendingAction || !user) return '';
    switch (pendingAction.type) {
      case 'toggle_admin':
        return user.is_admin ? 'Revoke Admin Access' : 'Grant Admin Access';
      case 'toggle_active':
        return user.is_active ? 'Deactivate Account' : 'Activate Account';
      case 'toggle_verified':
        return 'Change Verification Status';
      case 'send_password_reset':
        return 'Send Password Reset';
      case 'sftp_retry':
        return 'Retry SFTP Provisioning';
      case 'sftp_reset_password':
        return 'Reset SFTP Password';
      case 'toggle_source':
        return pendingAction.currentStatus === 'active' ? 'Pause Source' : 'Resume Source';
      case 'change_tier':
        return 'Change Subscription Tier';
      default:
        return 'Confirm Action';
    }
  };

  const getConfirmationMessage = (): string => {
    if (!pendingAction || !user) return '';
    switch (pendingAction.type) {
      case 'toggle_admin':
        return user.is_admin
          ? `Revoke admin access for ${user.email}? They will lose access to the admin panel.`
          : `Grant admin access to ${user.email}? They will have full admin panel access.`;
      case 'toggle_active':
        return user.is_active
          ? `Deactivate account for ${user.email}? They will be unable to log in.`
          : `Activate account for ${user.email}? They will be able to log in again.`;
      case 'toggle_verified':
        return user.is_verified
          ? `Remove verification status for ${user.email}?`
          : `Mark ${user.email} as verified?`;
      case 'send_password_reset':
        return `Send a password reset email to ${user.email}?`;
      case 'sftp_retry':
        return `Retry SFTP provisioning for ${user.email}? This will re-queue the provisioning process.`;
      case 'sftp_reset_password':
        return `Reset the SFTP password for ${user.email}? The current password will be invalidated.`;
      case 'toggle_source':
        return pendingAction.currentStatus === 'active'
          ? 'Pause this ingestion source? New files will not be processed until resumed.'
          : 'Resume this ingestion source? File processing will restart.';
      case 'change_tier': {
        const sub = user.subscription;
        return `Change ${user.email} from ${TIER_LABELS[sub?.tier || 'free']} to ${TIER_LABELS[pendingAction.newTier]}?`;
      }
      default:
        return '';
    }
  };

  const handleSavePricing = () => {
    if (!id || !user) return;
    // Validate all fields before showing confirmation
    const errors: Record<string, string> = {};
    for (const field of Object.keys(PRICING_LIMITS) as PricingField[]) {
      const value = pricingForm[field] as number | undefined | null;
      const error = validatePricingField(field, value);
      if (error) errors[field] = error;
    }
    if (pricingForm.deal_notes && pricingForm.deal_notes.length > 2000) {
      errors.deal_notes = 'Deal notes cannot exceed 2,000 characters';
    }
    if (Object.keys(errors).length > 0) {
      setPricingErrors(errors);
      return;
    }
    setShowPricingConfirmation(true);
  };

  const executeSavePricing = async () => {
    if (!id) return;
    setPricingLoading(true);
    try {
      const hasExisting = pricing?.custom_pricing !== null;
      const result = hasExisting
        ? await adminService.updateUserPricing(id, pricingForm)
        : await adminService.createUserPricing(id, pricingForm);
      setPricing(result);
      setShowPricingConfirmation(false);
      setShowPricingForm(false);
      setPricingForm({});
      const updated = await adminService.getUserDetail(id);
      setUser(updated);
      toast.success(hasExisting ? 'Custom pricing updated' : 'Custom pricing created');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save pricing';
      toast.error(message);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleRemovePricing = async () => {
    if (!id) return;
    setPricingLoading(true);
    try {
      const result = await adminService.deleteUserPricing(id);
      setPricing(result);
      setConfirmRemovePricing(false);
      setShowPricingForm(false);
      setPricingForm({});
      const updated = await adminService.getUserDetail(id);
      setUser(updated);
      toast.success('Custom pricing removed');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove pricing';
      toast.error(message);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleEditPricing = () => {
    if (pricing?.custom_pricing) {
      setPricingForm({
        custom_base_price: pricing.custom_pricing.custom_base_price ? parseFloat(pricing.custom_pricing.custom_base_price) : undefined,
        custom_minutes_included: pricing.custom_pricing.custom_minutes_included ? parseFloat(pricing.custom_pricing.custom_minutes_included) : undefined,
        custom_overage_rate: pricing.custom_pricing.custom_overage_rate ? parseFloat(pricing.custom_pricing.custom_overage_rate) : undefined,
        custom_overage_cap: pricing.custom_pricing.custom_overage_cap ?? undefined,
        deal_notes: pricing.custom_pricing.deal_notes ?? undefined,
      });
    }
    setPricingErrors({});
    setShowPricingForm(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-white rounded-xl shadow" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">User not found</p>
      </div>
    );
  }

  const sub = user.subscription;
  const usagePercent = sub
    ? Math.min(100, (parseFloat(sub.minutes_used) / parseFloat(sub.minutes_included)) * 100)
    : 0;

  const config = pendingAction ? CONFIRMATION_CONFIG[pendingAction.type] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/users"
            className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.email}</h1>
            <p className="text-sm text-gray-500">
              Joined {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPendingAction({ type: 'send_password_reset' })}
            className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            <EnvelopeIcon className="h-4 w-4" />
            Send Password Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* User Info Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="text-sm font-medium text-gray-900">{user.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Phone</dt>
              <dd className="text-sm font-medium text-gray-900">{user.phone_number}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Product Line</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">{user.product_line}</dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Verified</dt>
              <dd>
                <button
                  onClick={() => setPendingAction({ type: 'toggle_verified' })}
                  disabled={updating}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                    user.is_verified ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      user.is_verified ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Active</dt>
              <dd>
                <button
                  onClick={() => setPendingAction({ type: 'toggle_active' })}
                  disabled={updating}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                    user.is_active ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      user.is_active ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Admin</dt>
              <dd>
                <button
                  onClick={() => setPendingAction({ type: 'toggle_admin' })}
                  disabled={updating}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${
                    user.is_admin ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      user.is_admin ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </dd>
            </div>
          </dl>
        </div>

        {/* Subscription Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription</h3>
          {sub ? (
            <dl className="space-y-3">
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Tier</dt>
                <dd className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {TIER_LABELS[sub.tier] || sub.tier}
                  </span>
                  {sub.pending_tier && (
                    <span className="text-xs text-orange-600">
                      (downgrade to {TIER_LABELS[sub.pending_tier]} pending)
                    </span>
                  )}
                  {/* Tier change buttons */}
                  <div className="ml-2 flex gap-1">
                    {['free', 'starter', 'growth'].filter(t => t !== sub.tier).map(t => (
                      <button
                        key={t}
                        onClick={() => setPendingAction({ type: 'change_tier', newTier: t })}
                        className="rounded px-2 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50"
                      >
                        {TIER_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Status</dt>
                <dd className="text-sm font-medium text-gray-900 capitalize">{sub.status}</dd>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">Usage</span>
                  <span className="font-medium text-gray-900">
                    {parseFloat(sub.minutes_used).toFixed(0)} / {parseFloat(sub.minutes_included).toFixed(0)} min
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usagePercent > 90 ? 'bg-red-500' : usagePercent > 70 ? 'bg-yellow-500' : 'bg-indigo-500'
                    }`}
                    style={{ width: `${Math.min(100, usagePercent)}%` }}
                  />
                </div>
              </div>
              {parseFloat(sub.overage_minutes) > 0 && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Overage</dt>
                  <dd className="text-sm font-medium text-red-600">
                    {parseFloat(sub.overage_minutes).toFixed(0)} min
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Overage Cap</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {sub.overage_cap_override ?? sub.overage_cap} min
                  {sub.overage_cap_override !== null && (
                    <span className="ml-1 text-xs text-orange-600">(override)</span>
                  )}
                </dd>
              </div>
              {sub.stripe_customer_id && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Stripe Customer</dt>
                  <dd className="text-sm font-mono text-gray-600">{sub.stripe_customer_id}</dd>
                </div>
              )}
              {sub.current_period_end && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Period Ends</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(sub.current_period_end).toLocaleDateString()}
                  </dd>
                </div>
              )}
            </dl>
          ) : (
            <p className="text-sm text-gray-500">No subscription</p>
          )}
        </div>

        {/* Custom Pricing Card */}
        <div className="rounded-lg bg-white shadow p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Custom Pricing</h3>
            {pricing?.custom_pricing && (
              <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                Custom Deal
              </span>
            )}
          </div>

          {!showPricingForm ? (
            <>
              {pricing?.custom_pricing ? (
                <div className="space-y-4">
                  {pricing.custom_pricing.deal_notes && (
                    <div className="rounded-md bg-gray-50 p-3">
                      <p className="text-xs text-gray-500 mb-1">Deal Notes</p>
                      <p className="text-sm text-gray-700">{pricing.custom_pricing.deal_notes}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleEditPricing}
                      className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Edit Pricing
                    </button>
                    <button
                      onClick={() => setConfirmRemovePricing(true)}
                      className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50"
                    >
                      Remove Custom Pricing
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    Standard {TIER_LABELS[sub?.tier || 'free']} pricing applies.
                  </p>
                  <button
                    onClick={() => {
                      // Pre-populate from effective_pricing, or fall back to tier defaults
                      if (pricing?.effective_pricing) {
                        setPricingForm({
                          custom_base_price: parseFloat(pricing.effective_pricing.base_price),
                          custom_minutes_included: parseFloat(pricing.effective_pricing.minutes_included),
                          custom_overage_rate: parseFloat(pricing.effective_pricing.overage_rate),
                          custom_overage_cap: pricing.effective_pricing.overage_cap,
                        });
                      } else {
                        const td = TIER_DEFAULTS[sub?.tier || 'free'];
                        setPricingForm({
                          custom_base_price: td.price,
                          custom_minutes_included: td.minutes,
                          custom_overage_rate: td.overageRate,
                          custom_overage_cap: td.overageCap,
                        });
                      }
                      setPricingErrors({});
                      setShowPricingForm(true);
                    }}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    Configure Custom Pricing
                  </button>
                </div>
              )}

              {/* Billing Summary Table — always visible */}
              {(() => {
                const td = TIER_DEFAULTS[sub?.tier || 'free'] || TIER_DEFAULTS.free;
                const ep = pricing?.effective_pricing;
                const basePrice = ep ? parseFloat(ep.base_price) : td.price;
                const included = ep ? parseFloat(ep.minutes_included) : td.minutes;
                const overageRate = ep ? parseFloat(ep.overage_rate) : td.overageRate;
                const overageCap = ep ? ep.overage_cap : td.overageCap;
                const maxUsage = included + overageCap;
                const maxOverageRev = overageCap * overageRate;
                const monthlyTotal = basePrice + maxOverageRev;
                const effIncl = included > 0 ? basePrice / included : 0;
                const effMax = maxUsage > 0 ? monthlyTotal / maxUsage : 0;

                return (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Billing Summary</h4>
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        <tr>
                          <td className="py-2 pr-4 font-medium text-gray-700">Base Price</td>
                          <td className="py-2 text-right text-gray-900">${basePrice.toFixed(2)}/mo</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-medium text-gray-700">Included Minutes</td>
                          <td className="py-2 text-right text-gray-900">{included.toLocaleString()} min</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-medium text-gray-700">Overage Rate</td>
                          <td className="py-2 text-right text-gray-900">${overageRate.toFixed(4)}/min</td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4 font-medium text-gray-700">Overage Cap</td>
                          <td className="py-2 text-right text-gray-900">{overageCap.toLocaleString()} min</td>
                        </tr>

                        {/* Max monthly cost section */}
                        <tr className="border-t-2 border-gray-300">
                          <td colSpan={2} className="pt-2 pb-1 text-xs font-medium text-gray-500">
                            Max monthly cost at {maxUsage.toLocaleString()} min <span className="font-normal text-gray-400">(included + overage cap)</span>
                          </td>
                        </tr>
                        <tr className="text-orange-700">
                          <td className="py-1 pr-4 font-medium">Max Overage Revenue</td>
                          <td className="py-1 text-right">${maxOverageRev.toFixed(2)}</td>
                        </tr>
                        <tr className="font-semibold">
                          <td className="py-2 pr-4 text-gray-900">Monthly Total</td>
                          <td className="py-2 text-right text-gray-900">${monthlyTotal.toFixed(2)}</td>
                        </tr>

                        {/* Effective per-minute rates */}
                        <tr className="border-t-2 border-gray-300">
                          <td colSpan={2} className="pt-2 pb-1 text-xs font-medium text-gray-500">Effective rate per minute</td>
                        </tr>
                        <tr>
                          <td className="py-1 pr-4 text-gray-600">At included min</td>
                          <td className="py-1 text-right text-gray-900">{included > 0 ? `$${effIncl.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                        </tr>
                        <tr>
                          <td className="py-1 pr-4 text-gray-600">At max capacity</td>
                          <td className="py-1 text-right text-gray-900">{maxUsage > 0 ? `$${effMax.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="space-y-4">
              {/* Context banner */}
              <div className="rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-700">
                Configuring pricing for <strong>{user.email}</strong> &mdash; {user.product_line.toUpperCase()} {TIER_LABELS[sub?.tier || 'free']} tier
              </div>
              {!sub?.stripe_subscription_id && (
                <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  This customer has no active payment method. Custom pricing will take effect when they subscribe.
                </div>
              )}

              {(() => {
                const tierKey = sub?.tier || 'free';
                const td = TIER_DEFAULTS[tierKey] || TIER_DEFAULTS.free;
                const inputBase = "block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm";
                const inputOk = `${inputBase} ring-gray-300`;
                const inputErr = `${inputBase} ring-red-300`;
                const hasErrors = Object.keys(pricingErrors).length > 0;

                const handleFieldChange = (field: string, value: number | undefined) => {
                  setPricingForm({ ...pricingForm, [field]: value });
                  if (pricingErrors[field]) {
                    const next = { ...pricingErrors };
                    delete next[field];
                    setPricingErrors(next);
                  }
                };

                const handleFieldBlur = (field: PricingField) => {
                  const value = pricingForm[field] as number | undefined | null;
                  const error = validatePricingField(field, value);
                  if (error) {
                    setPricingErrors({ ...pricingErrors, [field]: error });
                  }
                };

                // Form values with tier default fallbacks
                const customBase = pricingForm.custom_base_price ?? td.price;
                const customIncluded = pricingForm.custom_minutes_included ?? td.minutes;
                const customOverageRate = pricingForm.custom_overage_rate ?? td.overageRate;
                const customOverageCap = pricingForm.custom_overage_cap ?? td.overageCap;

                // Coherence warnings: detect when field combinations create logical issues
                const coherence: Record<string, string> = {};
                if (customOverageCap > 0 && customOverageRate === 0) {
                  coherence.custom_overage_rate = `${customOverageCap.toLocaleString()} overage min allowed at $0 rate — no overage revenue`;
                }
                if (customIncluded === 0 && customOverageCap === 0) {
                  coherence.custom_minutes_included = `No included minutes and no overage allowed — customer blocked immediately`;
                  coherence.custom_overage_cap = `No overage allowed — customer capped at 0 min`;
                }

                const coherenceRing = `${inputBase} ring-amber-400`;

                return (
                  <>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Base Price ($/mo)</label>
                        <input
                          type="number" step="0.01" min="0" max="1000000"
                          value={pricingForm.custom_base_price ?? ''}
                          onChange={(e) => handleFieldChange('custom_base_price', e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur('custom_base_price')}
                          className={pricingErrors.custom_base_price ? inputErr : inputOk}
                        />
                        {pricingErrors.custom_base_price && <p className="mt-1 text-xs text-red-600">{pricingErrors.custom_base_price}</p>}
                        <p className="mt-1 text-xs text-gray-400">Tier default: ${td.price.toFixed(2)}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Included Minutes</label>
                        <input
                          type="number" step="1" min="0" max="50000000"
                          value={pricingForm.custom_minutes_included ?? ''}
                          onChange={(e) => handleFieldChange('custom_minutes_included', e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur('custom_minutes_included')}
                          className={pricingErrors.custom_minutes_included ? inputErr : coherence.custom_minutes_included ? coherenceRing : inputOk}
                        />
                        {pricingErrors.custom_minutes_included && <p className="mt-1 text-xs text-red-600">{pricingErrors.custom_minutes_included}</p>}
                        {!pricingErrors.custom_minutes_included && coherence.custom_minutes_included && <p className="mt-1 text-xs text-amber-600">{coherence.custom_minutes_included}</p>}
                        <p className="mt-1 text-xs text-gray-400">Tier default: {td.minutes.toLocaleString()}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Overage Rate ($/min)</label>
                        <input
                          type="number" step="0.0001" min="0" max="1"
                          value={pricingForm.custom_overage_rate ?? ''}
                          onChange={(e) => handleFieldChange('custom_overage_rate', e.target.value ? parseFloat(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur('custom_overage_rate')}
                          className={pricingErrors.custom_overage_rate ? inputErr : coherence.custom_overage_rate ? coherenceRing : inputOk}
                        />
                        {pricingErrors.custom_overage_rate && <p className="mt-1 text-xs text-red-600">{pricingErrors.custom_overage_rate}</p>}
                        {!pricingErrors.custom_overage_rate && coherence.custom_overage_rate && <p className="mt-1 text-xs text-amber-600">{coherence.custom_overage_rate}</p>}
                        <p className="mt-1 text-xs text-gray-400">Tier default: ${td.overageRate.toFixed(4)}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Overage Cap (min)</label>
                        <input
                          type="number" step="1" min="0" max="50000000"
                          value={pricingForm.custom_overage_cap ?? ''}
                          onChange={(e) => handleFieldChange('custom_overage_cap', e.target.value ? parseInt(e.target.value) : undefined)}
                          onBlur={() => handleFieldBlur('custom_overage_cap')}
                          className={pricingErrors.custom_overage_cap ? inputErr : coherence.custom_overage_cap ? coherenceRing : inputOk}
                        />
                        {pricingErrors.custom_overage_cap && <p className="mt-1 text-xs text-red-600">{pricingErrors.custom_overage_cap}</p>}
                        {!pricingErrors.custom_overage_cap && coherence.custom_overage_cap && <p className="mt-1 text-xs text-amber-600">{coherence.custom_overage_cap}</p>}
                        <p className="mt-1 text-xs text-gray-400">Tier default: {td.overageCap.toLocaleString()}</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Deal Notes</label>
                      <textarea
                        rows={2}
                        maxLength={2000}
                        value={pricingForm.deal_notes ?? ''}
                        onChange={(e) => {
                          setPricingForm({ ...pricingForm, deal_notes: e.target.value || undefined });
                          if (pricingErrors.deal_notes) {
                            const next = { ...pricingErrors };
                            delete next.deal_notes;
                            setPricingErrors(next);
                          }
                        }}
                        placeholder="Internal notes about this deal..."
                        className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm ${pricingErrors.deal_notes ? 'ring-red-300' : 'ring-gray-300'}`}
                      />
                      {pricingErrors.deal_notes && <p className="mt-1 text-xs text-red-600">{pricingErrors.deal_notes}</p>}
                      <p className="mt-1 text-xs text-gray-400">{(pricingForm.deal_notes || '').length}/2,000</p>
                    </div>

                    {/* Pricing Impact — unified before/after table */}
                    {(() => {
                      const beforeBase = pricing?.custom_pricing?.custom_base_price ? parseFloat(pricing.custom_pricing.custom_base_price) : td.price;
                      const beforeIncluded = pricing?.custom_pricing?.custom_minutes_included ? parseFloat(pricing.custom_pricing.custom_minutes_included) : td.minutes;
                      const beforeRate = pricing?.custom_pricing?.custom_overage_rate ? parseFloat(pricing.custom_pricing.custom_overage_rate) : td.overageRate;
                      const beforeCap = pricing?.custom_pricing?.custom_overage_cap ?? td.overageCap;
                      const maxUsage = customIncluded + customOverageCap;
                      const beforeBk = calculateBreakdown(maxUsage, beforeBase, beforeIncluded, beforeRate, beforeCap);
                      const afterBk = calculateBreakdown(maxUsage, customBase, customIncluded, customOverageRate, customOverageCap);
                      const netChange = afterBk.total - beforeBk.total;

                      const changes = buildPricingChanges(pricingForm, pricing?.custom_pricing ?? null, td);

                      const fmtVal = (v: number, fmt: 'currency' | 'currency_rate' | 'integer', unit?: string) => {
                        let s: string;
                        switch (fmt) {
                          case 'currency': s = `$${v.toFixed(2)}`; break;
                          case 'currency_rate': s = `$${v.toFixed(4)}`; break;
                          case 'integer': s = v.toLocaleString(); break;
                        }
                        return unit ? `${s} ${unit}` : s;
                      };

                      const fmtDelta = (v: number, fmt: 'currency' | 'currency_rate' | 'integer') => {
                        const a = Math.abs(v);
                        switch (fmt) {
                          case 'currency': return `$${a.toFixed(2)}`;
                          case 'currency_rate': return `$${a.toFixed(4)}`;
                          case 'integer': return a.toLocaleString();
                        }
                      };

                      const rowColor = (c: PricingFieldChange) => {
                        const d = c.afterValue - c.beforeValue;
                        if (d === 0) return { text: 'text-gray-400', bg: '' };
                        const fav = (c.favorableDirection === 'up' && d > 0) || (c.favorableDirection === 'down' && d < 0);
                        return { text: fav ? 'text-green-700' : 'text-red-700', bg: fav ? 'bg-green-50' : 'bg-red-50' };
                      };

                      const totalColor = netChange === 0 ? 'text-gray-900' : netChange < 0 ? 'text-green-800' : 'text-red-800';
                      const totalBg = netChange === 0 ? '' : netChange < 0 ? 'bg-green-50' : 'bg-red-50';

                      return (
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Pricing Impact</h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="pb-2 text-left font-medium text-gray-500"></th>
                                <th className="pb-2 text-right font-medium text-gray-500">Before</th>
                                <th className="pb-2 text-right font-medium text-gray-500">After</th>
                                <th className="pb-2 text-right font-medium text-gray-500">Change</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {changes.map((c) => {
                                const delta = c.afterValue - c.beforeValue;
                                const unchanged = delta === 0;
                                const rc = rowColor(c);
                                return (
                                  <tr key={c.label} className={`${unchanged ? 'opacity-40' : ''} ${rc.bg}`}>
                                    <td className="py-2 pr-4 font-medium text-gray-700">{c.label}</td>
                                    <td className="py-2 text-right text-gray-600">{fmtVal(c.beforeValue, c.format, c.unit)}</td>
                                    <td className={`py-2 text-right font-medium ${unchanged ? 'text-gray-600' : 'text-gray-900'}`}>{fmtVal(c.afterValue, c.format, c.unit)}</td>
                                    <td className={`py-2 pl-4 text-right font-medium ${rc.text}`}>
                                      {unchanged
                                        ? <span className="text-gray-300">&mdash;</span>
                                        : <span>{delta > 0 ? '+' : '-'}{fmtDelta(delta, c.format)} {delta > 0 ? '\u2191' : '\u2193'}</span>}
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* Separator + impact summary rows */}
                              <tr className="border-t-2 border-gray-300">
                                <td colSpan={4} className="pt-2 pb-1 text-xs font-medium text-gray-500">
                                  Max monthly cost at {maxUsage.toLocaleString()} min <span className="font-normal text-gray-400">(included + overage cap)</span>
                                </td>
                              </tr>
                              {(() => {
                                const beforeMaxOvRev = beforeCap * beforeRate;
                                const afterMaxOvRev = customOverageCap * customOverageRate;
                                const d = afterMaxOvRev - beforeMaxOvRev;
                                return (
                                  <tr className="text-orange-700">
                                    <td className="py-1 pr-4 font-medium">Max Overage Revenue</td>
                                    <td className="py-1 text-right">${beforeMaxOvRev.toFixed(2)}</td>
                                    <td className="py-1 text-right font-medium">${afterMaxOvRev.toFixed(2)}</td>
                                    <td className="py-1 pl-4 text-right font-medium">
                                      {Math.abs(d) < 0.005
                                        ? <span className="text-gray-300">&mdash;</span>
                                        : <span className={d < 0 ? 'text-red-700' : 'text-green-700'}>{d > 0 ? '+' : '-'}${Math.abs(d).toFixed(2)} {d > 0 ? '\u2191' : '\u2193'}</span>}
                                    </td>
                                  </tr>
                                );
                              })()}
                              <tr className={`font-semibold ${totalBg}`}>
                                <td className="py-2 pr-4 text-gray-900">Monthly Total</td>
                                <td className="py-2 text-right text-gray-900">${beforeBk.total.toFixed(2)}</td>
                                <td className={`py-2 text-right ${totalColor}`}>${afterBk.total.toFixed(2)}</td>
                                <td className={`py-2 pl-4 text-right ${totalColor}`}>
                                  {netChange === 0
                                    ? <span className="text-gray-300">&mdash;</span>
                                    : <span>{netChange > 0 ? '+' : '-'}${Math.abs(netChange).toFixed(2)} {netChange > 0 ? '\u2191' : '\u2193'}</span>}
                                </td>
                              </tr>

                              {/* Effective per-minute rates */}
                              <tr className="border-t-2 border-gray-300">
                                <td colSpan={4} className="pt-2 pb-1 text-xs font-medium text-gray-500">Effective rate per minute</td>
                              </tr>
                              {(() => {
                                const bEffIncl = beforeIncluded > 0 ? beforeBase / beforeIncluded : 0;
                                const aEffIncl = customIncluded > 0 ? customBase / customIncluded : 0;
                                const dIncl = aEffIncl - bEffIncl;

                                const bMaxMin = beforeIncluded + beforeCap;
                                const bMaxTotal = beforeBase + (beforeCap * beforeRate);
                                const bEffMax = bMaxMin > 0 ? bMaxTotal / bMaxMin : 0;

                                const aMaxMin = customIncluded + customOverageCap;
                                const aMaxTotal = customBase + (customOverageCap * customOverageRate);
                                const aEffMax = aMaxMin > 0 ? aMaxTotal / aMaxMin : 0;
                                const dMax = aEffMax - bEffMax;

                                return (
                                  <>
                                    <tr>
                                      <td className="py-1 pr-4 text-gray-600">At included min</td>
                                      <td className="py-1 text-right text-gray-600">{beforeIncluded > 0 ? `$${bEffIncl.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                                      <td className="py-1 text-right font-medium text-gray-900">{customIncluded > 0 ? `$${aEffIncl.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                                      <td className="py-1 pl-4 text-right font-medium">
                                        {beforeIncluded === 0 || customIncluded === 0 || Math.abs(dIncl) < 0.00005
                                          ? <span className="text-gray-300">&mdash;</span>
                                          : <span className={dIncl < 0 ? 'text-green-700' : 'text-red-700'}>{dIncl > 0 ? '+' : '-'}${Math.abs(dIncl).toFixed(4)}</span>}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="py-1 pr-4 text-gray-600">At max capacity</td>
                                      <td className="py-1 text-right text-gray-600">{bMaxMin > 0 ? `$${bEffMax.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                                      <td className="py-1 text-right font-medium text-gray-900">{aMaxMin > 0 ? `$${aEffMax.toFixed(4)}` : <span className="text-gray-400">&mdash;</span>}</td>
                                      <td className="py-1 pl-4 text-right font-medium">
                                        {bMaxMin === 0 || aMaxMin === 0 || Math.abs(dMax) < 0.00005
                                          ? <span className="text-gray-300">&mdash;</span>
                                          : <span className={dMax < 0 ? 'text-green-700' : 'text-red-700'}>{dMax > 0 ? '+' : '-'}${Math.abs(dMax).toFixed(4)}</span>}
                                      </td>
                                    </tr>
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>

                          {netChange !== 0 && (
                            <div className={`mt-3 rounded-md px-3 py-2 text-sm font-medium ${netChange < 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {netChange < 0
                                ? `Customer saves $${Math.abs(netChange).toFixed(2)}/mo at max usage`
                                : `Customer pays $${netChange.toFixed(2)}/mo more at max usage`}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex gap-2">
                      <button
                        onClick={handleSavePricing}
                        disabled={pricingLoading || hasErrors}
                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                      >
                        {pricingLoading ? 'Saving...' : 'Save Pricing'}
                      </button>
                      <button
                        onClick={() => { setShowPricingForm(false); setPricingForm({}); setPricingErrors({}); }}
                        className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Analysis Summary Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Analysis Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Total', value: user.analysis_counts.total, color: 'text-gray-900', status: '' },
              { label: 'Completed', value: user.analysis_counts.completed, color: 'text-green-600', status: 'completed' },
              { label: 'Processing', value: user.analysis_counts.processing + user.analysis_counts.queued, color: 'text-blue-600', status: 'processing' },
              { label: 'Failed', value: user.analysis_counts.failed, color: 'text-red-600', status: 'failed' },
            ].map((stat) => (
              <button
                key={stat.label}
                onClick={() => navigate(`/admin/users/${id}/analyses${stat.status ? `?status=${stat.status}` : ''}`)}
                className="text-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <p className={`text-2xl font-semibold ${stat.color}`}>{stat.value.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* SFTP Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <ServerStackIcon className="h-5 w-5 text-gray-400" />
              SFTP
            </h3>
            {sftp && (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                (SFTP_STATUS_BADGE[sftp.provisioning_status] || SFTP_STATUS_BADGE['pending']).className
              }`}>
                {(SFTP_STATUS_BADGE[sftp.provisioning_status] || SFTP_STATUS_BADGE['pending']).label}
              </span>
            )}
          </div>
          {sftp ? (
            <dl className="space-y-3">
              {sftp.username && (
                <div className="flex justify-between items-center">
                  <dt className="text-sm text-gray-500">Username</dt>
                  <dd className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-900">{sftp.username}</span>
                    <button
                      onClick={() => copyToClipboard(sftp.username!)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </dd>
                </div>
              )}
              {sftp.password && (
                <div className="flex justify-between items-center">
                  <dt className="text-sm text-gray-500">Password</dt>
                  <dd className="flex items-center gap-2">
                    <span className="text-sm font-mono text-gray-900">
                      {showPassword ? sftp.password : '••••••••'}
                    </span>
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(sftp.password!)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </dd>
                </div>
              )}
              {sftp.provisioned_at && (
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Provisioned</dt>
                  <dd className="text-sm text-gray-900">
                    {new Date(sftp.provisioned_at).toLocaleDateString()}
                  </dd>
                </div>
              )}
              {sftp.provisioning_error && (
                <div className="rounded-md bg-red-50 p-3 mt-2">
                  <p className="text-sm text-red-700">{sftp.provisioning_error}</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                {(sftp.provisioning_status === 'failed' || sftp.provisioning_status === 'pending') && (
                  <button
                    onClick={() => setPendingAction({ type: 'sftp_retry' })}
                    disabled={sftpLoading}
                    className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Retry Provisioning
                  </button>
                )}
                {sftp.provisioning_status === 'provisioned' && (
                  <button
                    onClick={() => setPendingAction({ type: 'sftp_reset_password' })}
                    disabled={sftpLoading}
                    className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <KeyIcon className="h-4 w-4" />
                    Reset Password
                  </button>
                )}
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-500">Loading SFTP info...</p>
          )}
        </div>

        {/* Usage Breakdown Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Breakdown</h3>
          {usage ? (
            usage.by_api_key.length > 0 ? (
              <>
                <table className="min-w-full divide-y divide-gray-200 mb-3">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">API Key</th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Minutes</th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Analyses</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {usage.by_api_key.map((item, i) => (
                      <tr key={i}>
                        <td className="py-2 text-sm text-gray-900">
                          {item.key_prefix ? (
                            <span className="font-mono">{item.key_prefix}...</span>
                          ) : (
                            <span className="text-gray-500">{item.description || 'Direct'}</span>
                          )}
                        </td>
                        <td className="py-2 text-sm text-gray-900 text-right">{item.minutes_used.toFixed(1)}</td>
                        <td className="py-2 text-sm text-gray-900 text-right">{item.analysis_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-between text-sm font-medium text-gray-900 pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>{usage.total_minutes.toFixed(1)} min / {usage.total_analyses} analyses</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">No usage recorded</p>
            )
          ) : (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
        </div>

        {/* Integrations Card */}
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Integrations</h3>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">API Keys</dt>
              <dd className="text-sm font-medium text-gray-900">{user.api_keys_count}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Ingestion Sources</dt>
              <dd className="text-sm font-medium text-gray-900">{user.ingestion_sources_count}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Ingestion Sources Card (full width) */}
      {sources && sources.length > 0 && (
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Ingestion Sources</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Name</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Type</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Last Sync</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Files</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Last Error</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sources.map((source) => {
                  const typeBadge = SOURCE_TYPE_BADGE[source.source_type] || SOURCE_TYPE_BADGE['web_upload'];
                  const statusBadge = SOURCE_STATUS_BADGE[source.status] || SOURCE_STATUS_BADGE['disabled'];
                  return (
                    <tr key={source.id}>
                      <td className="py-2 text-sm text-gray-900">{source.name}</td>
                      <td className="py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge.className}`}>
                          {typeBadge.label}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="py-2 text-sm text-gray-500">
                        {source.last_sync_at ? new Date(source.last_sync_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-2 text-sm text-gray-900 text-right">{source.total_files_processed}</td>
                      <td className="py-2 text-sm text-red-600 max-w-xs truncate">
                        {source.last_error || '-'}
                      </td>
                      <td className="py-2 text-right">
                        {(source.status === 'active' || source.status === 'paused') && (
                          <button
                            onClick={() => setPendingAction({ type: 'toggle_source', sourceId: source.id, currentStatus: source.status })}
                            disabled={sourcesLoading}
                            className="rounded px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
                          >
                            {source.status === 'active' ? 'Pause' : 'Resume'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Evaluation Criteria editor (full width) */}
      {id && <EvaluationCriteriaEditor userId={id} />}

      {/* Cost-to-Serve (full width) */}
      {user && id && <CostToServePanel userId={id} />}

      {/* Customer Activity Timeline (full width) */}
      {timeline && timeline.events.length > 0 && (
        <div className="rounded-xl bg-white shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-gray-400" />
            Activity Timeline
          </h3>
          <div className="flow-root">
            <ul className="-mb-8">
              {timeline.events.map((event, idx) => {
                const iconStyle = TIMELINE_ICONS[event.event_type] || { color: 'bg-gray-400' };
                const isLast = idx === timeline.events.length - 1;
                return (
                  <li key={idx}>
                    <div className="relative pb-8">
                      {!isLast && (
                        <span className="absolute left-3 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                      )}
                      <div className="relative flex items-start space-x-3">
                        <div className={`relative flex h-6 w-6 items-center justify-center rounded-full ${iconStyle.color}`}>
                          <div className="h-2 w-2 rounded-full bg-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-900">{event.description}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Remove Custom Pricing Confirmation Dialog */}
      {confirmRemovePricing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500/75" onClick={() => setConfirmRemovePricing(false)} />
          <div className="relative z-10 w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Custom Pricing</h3>
            <p className="text-sm text-gray-600 mb-4">
              Remove custom pricing for <strong>{user.email}</strong>? They will revert to standard{' '}
              <strong>{TIER_LABELS[sub?.tier || 'free']}</strong> tier pricing.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRemovePricing(false)}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRemovePricing}
                disabled={pricingLoading}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
              >
                {pricingLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Pricing Confirmation */}
      <ConfirmationDialog
        isOpen={showPricingConfirmation}
        onClose={() => setShowPricingConfirmation(false)}
        onConfirm={executeSavePricing}
        title={pricing?.custom_pricing ? 'Update Custom Pricing' : 'Set Custom Pricing'}
        message={`Apply these pricing changes for ${user.email}?`}
        confirmLabel="Apply Changes"
        loading={pricingLoading}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title={getConfirmationTitle()}
        message={getConfirmationMessage()}
        confirmLabel={config?.confirmLabel || 'Confirm'}
        variant={config?.variant || 'default'}
        loading={actionLoading}
      />
    </div>
  );
};

export default AdminUserDetail;
